import { useForm } from "@tanstack/react-form";
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { useRef } from "react";
import { useNavigate } from "react-router";
import { api } from "../../../convex/_generated/api";
import { Button } from "../../components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "../../components/ui/field";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";

function slugify(raw: string): string {
	return raw
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 64);
}

/** Dollar string → integer cents; null if invalid (>2 decimals, negative). */
function dollarsToCents(input: string): number | null {
	const s = input.trim();
	if (s === "") return 0;
	if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
	return Math.round(Number.parseFloat(s) * 100);
}

export default function CreateFirmDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const createFirm = useMutation(api.staff.createFirm);
	const navigate = useNavigate();
	const slugDirtyRef = useRef(false);

	const form = useForm({
		defaultValues: { name: "", slug: "", onboardingFee: "" },
		onSubmit: async ({ value }) => {
			const slug = slugDirtyRef.current ? value.slug : slugify(value.name);
			if (!value.name.trim() || !slug) return;
			const onboardingFeePerSeatCents = dollarsToCents(value.onboardingFee);
			if (onboardingFeePerSeatCents === null) return;
			mutate({ name: value.name.trim(), slug, onboardingFeePerSeatCents });
		},
	});

	const { mutate, isPending, error } = useReactQueryMutation({
		mutationFn: async (args: {
			name: string;
			slug: string;
			onboardingFeePerSeatCents: number;
		}) => {
			return await createFirm(args);
		},
		onSuccess: (result) => {
			onOpenChange(false);
			form.reset();
			slugDirtyRef.current = false;
			if (result) navigate(`/staff/firms/${result.tenantId}`);
		},
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<ScrollArea className="max-h-[calc(100vh-4rem)]">
					<div className="flex flex-col gap-4">
						<DialogHeader>
							<DialogTitle>Create firm</DialogTitle>
							<DialogDescription>
								Set up a new workspace. You can pre-register members once the
								firm exists.
							</DialogDescription>
						</DialogHeader>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								form.handleSubmit();
							}}
						>
							<FieldGroup>
								<form.Field name="name">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="firm-name">Firm name</FieldLabel>
											<Input
												id="firm-name"
												value={field.state.value}
												onChange={(e) => {
													field.handleChange(e.target.value);
													if (!slugDirtyRef.current) {
														form.setFieldValue("slug", slugify(e.target.value));
													}
												}}
												onBlur={field.handleBlur}
												placeholder="Acme Legal"
												required
												autoFocus
											/>
										</Field>
									)}
								</form.Field>
								<form.Field name="slug">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="firm-slug">Slug</FieldLabel>
											<Input
												id="firm-slug"
												value={field.state.value}
												onChange={(e) => {
													field.handleChange(e.target.value);
													slugDirtyRef.current = true;
												}}
												onBlur={field.handleBlur}
												placeholder="acme-legal"
												required
											/>
											<FieldDescription>
												Lowercase letters, numbers, and dashes. Auto-generated
												from the name; edit to override.
											</FieldDescription>
										</Field>
									)}
								</form.Field>
								<form.Field
									name="onboardingFee"
									validators={{
										onChange: ({ value }) =>
											dollarsToCents(value) === null
												? "Enter dollars with at most two decimals"
												: undefined,
									}}
								>
									{(field) => (
										<Field>
											<FieldLabel htmlFor="firm-onboarding-fee">
												Onboarding fee / seat
											</FieldLabel>
											<Input
												id="firm-onboarding-fee"
												inputMode="decimal"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="0.00"
											/>
											{field.state.meta.errors.length > 0 ? (
												<p className="text-sm text-destructive">
													{field.state.meta.errors[0]}
												</p>
											) : null}
											<FieldDescription>
												One-time charge per seat when this firm adds capacity.
												Dollars; blank or 0 = no fee.
											</FieldDescription>
										</Field>
									)}
								</form.Field>
								{error ? (
									<Field>
										<p className="text-sm text-destructive">
											{error instanceof Error ? error.message : String(error)}
										</p>
									</Field>
								) : null}
								<DialogFooter>
									<DialogClose
										render={<Button variant="outline" type="button" />}
									>
										Cancel
									</DialogClose>
									<form.Subscribe
										selector={(state) => [state.values.name, state.values.slug]}
									>
										{([name, slug]) => (
											<Button
												type="submit"
												disabled={
													isPending ||
													!(name as string).trim() ||
													!(slugDirtyRef.current
														? (slug as string)
														: slugify(name as string))
												}
											>
												{isPending ? "Creating..." : "Create firm"}
											</Button>
										)}
									</form.Subscribe>
								</DialogFooter>
							</FieldGroup>
						</form>
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
}
